import { useState, useEffect, useMemo } from 'react';
import {
  Building2,
  Plus,
  Search,
  PoundSterling,
  Clock,
  Receipt,
  CalendarDays,
  Users,
  ChevronRight,
  ShoppingCart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getCompanies } from '@/services/integrationsRedesignService';
import type {
  CompanyWithIntegrations,
  SimpleIntegrationConfig,
} from '@/types/integrations-redesign';
import {
  FINANCE_PRODUCT_LABELS,
  TIMESHEET_PRODUCT_LABELS,
  EXPENSE_PRODUCT_LABELS,
  PLANNER_PRODUCT_LABELS,
  PURCHASING_PRODUCT_LABELS,
} from '@/types/integrations-redesign';
import { CompanySheet } from '@/components/integrations/CompanySheet';
import { ConnectionStatusBadge } from '@/components/integrations/ConnectionStatusBadge';
import { useDataMode } from '@/contexts/DataModeContext';

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

interface IntegrationCellProps {
  config: SimpleIntegrationConfig | undefined | null;
  labels: Record<string, string>;
}

function IntegrationCell({ config, labels }: IntegrationCellProps) {
  if (!config || config.status === 'not_configured' || !config.product) {
    return (
      <span className="text-sm text-muted-foreground italic">Not configured</span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium">
        {labels[config.product] ?? config.product}
      </span>
      <ConnectionStatusBadge status={config.status} compact />
    </div>
  );
}

function TableRowSkeleton() {
  return (
    <tr className="border-b">
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-full" />
        </td>
      ))}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function SettingsIntegrations() {
  const [companies, setCompanies] = useState<CompanyWithIntegrations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithIntegrations | null>(null);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const { mode } = useDataMode();

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadCompanies = async () => {
    setIsLoading(true);
    try {
      const data = await getCompanies();
      setCompanies(data);
    } catch {
      // toast error if toast is available
    } finally {
      setIsLoading(false);
    }
  };

  // Reload companies on mount and when the data mode changes (Demo ↔ Live)
  useEffect(() => {
    loadCompanies();
  }, [mode]);

  // -------------------------------------------------------------------------
  // Filtered list
  // -------------------------------------------------------------------------

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return companies.filter(
      (c) =>
        (c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)) &&
        (statusFilter === 'all' || c.status === statusFilter),
    );
  }, [companies, searchQuery, statusFilter]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Integrations</h2>
          <p className="text-muted-foreground mt-1">
            Manage company integrations and connections
          </p>
        </div>
        <Button onClick={() => setShowCreateSheet(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Search & filter row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-9"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as 'all' | 'active' | 'inactive')}
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Companies table */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/40">
              <th className="text-left text-sm text-muted-foreground font-medium px-4 py-3">
                Company
              </th>
              <th className="text-left text-sm text-muted-foreground font-medium px-4 py-3">
                <PoundSterling className="h-3.5 w-3.5 inline mr-1" />
                Financial
              </th>
              <th className="text-left text-sm text-muted-foreground font-medium px-4 py-3">
                <CalendarDays className="h-3.5 w-3.5 inline mr-1" />
                Planner
              </th>
              <th className="text-left text-sm text-muted-foreground font-medium px-4 py-3">
                <ShoppingCart className="h-3.5 w-3.5 inline mr-1" />
                Purchasing
              </th>
              <th className="text-left text-sm text-muted-foreground font-medium px-4 py-3">
                <Clock className="h-3.5 w-3.5 inline mr-1" />
                Timesheets
              </th>
              <th className="text-left text-sm text-muted-foreground font-medium px-4 py-3">
                <Receipt className="h-3.5 w-3.5 inline mr-1" />
                Expense
              </th>
              <th className="text-left text-sm text-muted-foreground font-medium px-4 py-3">
                <Users className="h-3.5 w-3.5 inline mr-1" />
                Users
              </th>
              <th className="text-left text-sm text-muted-foreground font-medium px-4 py-3">
                Status
              </th>
              <th className="w-10 px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Building2 className="h-10 w-10 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">No companies found</p>
                    {searchQuery && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Try{' '}
                        <button
                          className="underline"
                          onClick={() => {
                            setSearchQuery('');
                            setStatusFilter('all');
                          }}
                        >
                          clearing the filter
                        </button>
                      </p>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((company) => (
                <tr
                  key={company.id}
                  className={`border-b last:border-0 cursor-pointer hover:bg-muted/50 transition-colors ${
                    selectedCompany?.id === company.id ? 'bg-muted/60' : ''
                  }`}
                  onClick={() => setSelectedCompany(company)}
                >
                  {/* Company name + code */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{company.name}</p>
                        <p className="text-xs text-muted-foreground">{company.code}</p>
                      </div>
                    </div>
                  </td>

                  {/* Financial */}
                  <td className="px-4 py-3">
                    <IntegrationCell
                      config={company.financeIntegration}
                      labels={FINANCE_PRODUCT_LABELS}
                    />
                  </td>

                  {/* Planner */}
                  <td className="px-4 py-3">
                    <IntegrationCell
                      config={company.plannerIntegration}
                      labels={PLANNER_PRODUCT_LABELS}
                    />
                  </td>

                  {/* Purchasing */}
                  <td className="px-4 py-3">
                    <IntegrationCell
                      config={company.purchasingIntegration}
                      labels={PURCHASING_PRODUCT_LABELS}
                    />
                  </td>

                  {/* Timesheets */}
                  <td className="px-4 py-3">
                    <IntegrationCell
                      config={company.timesheetIntegration}
                      labels={TIMESHEET_PRODUCT_LABELS}
                    />
                  </td>

                  {/* Expense */}
                  <td className="px-4 py-3">
                    <IntegrationCell
                      config={company.expenseIntegration}
                      labels={EXPENSE_PRODUCT_LABELS}
                    />
                  </td>

                  {/* Users */}
                  <td className="px-4 py-3">
                    <span className="text-sm tabular-nums">{company.userCount}</span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <Badge
                      variant={company.status === 'active' ? 'default' : 'secondary'}
                      className="capitalize"
                    >
                      {company.status}
                    </Badge>
                  </td>

                  {/* Chevron */}
                  <td className="px-4 py-3 w-10">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View / edit sheet — opens when a row is clicked */}
      <CompanySheet
        company={selectedCompany}
        open={!!selectedCompany}
        onOpenChange={(open) => {
          if (!open) setSelectedCompany(null);
        }}
        onCompanyUpdated={(updated) => {
          setCompanies((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c)),
          );
          setSelectedCompany(updated);
        }}
        onCompanyCreated={async () => {
          setShowCreateSheet(false);
          await loadCompanies();
        }}
        existingCodes={companies.map((c) => c.code)}
      />

      {/* Create sheet — opens from the Add Company button */}
      <CompanySheet
        company={null}
        open={showCreateSheet}
        onOpenChange={setShowCreateSheet}
        onCompanyUpdated={() => {}}
        onCompanyCreated={async (created) => {
          setShowCreateSheet(false);
          await loadCompanies();
          setSelectedCompany(created);
        }}
        existingCodes={companies.map((c) => c.code)}
      />
    </div>
  );
}

export default SettingsIntegrations;

